// src/scenes/game-components/PlayerController.js
// 🎮 PlayerController - UPDATED: Mobile touch controls for gameplay
// Manages input, jumping, special abilities, and player physics with mobile support

import { gameState } from '../../game/state.js';
import { GAME_CONFIG } from '../../game/config.js';

export default class PlayerController {
  constructor(scene) {
    this.scene = scene;
    this.cursors = null;
    this.wasdKeys = null;
    this.mobileControls = {
      left: false,
      right: false,
      jump: false,
      ability: false
    };
    this.touchButtons = {};
    this.isMobile = false;
  }

  createPlayer() {
    const { width, height } = this.scene.scale;
    const physicsGroups = this.scene.getPhysicsGroups();
    
    const spriteKey = gameState.character === 'BRIDE' ? 'bride' : 'groom';
    // FIXED: Position player properly on ground surface (physics ground at height - 30)
    const groundLevel = height - 30;
    const playerY = groundLevel - 32; // Player standing on ground surface
    const player = this.scene.physics.add.sprite(150, playerY, spriteKey);
    
    // FIXED: Use config scaling
    player.setScale(GAME_CONFIG.SPRITES.SCALE.PLAYER);
    player.setBounce(0);
    player.setCollideWorldBounds(false);
    player.setDragX(400);
    
    // FIXED: Proper collision box
    player.body.setSize(16, 24); // Appropriate collision box
    
    // Enhanced player properties for double jump
    player.isInvincible = false;
    player.abilityReady = true;
    player.isGrounded = false;
    player.jumpCount = 0;
    player.maxJumps = 2; // Double jump enabled
    
    // Set player in scene
    this.scene.setPlayer(player);
    
    // Setup collision with ground
    this.scene.physics.add.collider(player, physicsGroups.ground);
    
    /* console.log(`👤 Created player: ${gameState.character} at ${player.x}, ${playerY} with scale ${GAME_CONFIG.SPRITES.SCALE.PLAYER}`); */
  }

  setupControls() {
    // Detect mobile device
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                   'ontouchstart' in window ||
                   this.scene.scale.width < 768;
    
    // Keyboard controls
    this.cursors = this.scene.input.keyboard.createCursorKeys();
    this.wasdKeys = this.scene.input.keyboard.addKeys('W,S,A,D,SPACE');
    
    // Create mobile controls if needed
    if (this.isMobile) {
      this.createMobileControls();
    }
    
    /* console.log(`🎮 Controls setup - Mobile: ${this.isMobile}`); */
  }

  createMobileControls() {
    const { width, height } = this.scene.scale;
    
    // Determine orientation and get appropriate settings
    const isLandscape = width > height;
    const positions = isLandscape ? 
      GAME_CONFIG.MOBILE.POSITIONS.LANDSCAPE : 
      GAME_CONFIG.MOBILE.POSITIONS.PORTRAIT;
    
    // Calculate BIGGER responsive button size
    const baseButtonSize = Math.min(
      GAME_CONFIG.MOBILE.BUTTON_SIZE, 
      Math.min(width, height) / GAME_CONFIG.MOBILE.LANDSCAPE.BUTTON_SCALE_FACTOR
    );
    
    // Make buttons even bigger on small screens
    const buttonSize = Math.max(baseButtonSize, 80); // Minimum 80px
    
    console.log(`📱 Creating BIGGER mobile controls - ${isLandscape ? 'Landscape' : 'Portrait'} mode (${buttonSize}px buttons)`);
    
    // VERTICAL MOVEMENT CONTROLS (Left side) - BIGGER
    // Forward/Right movement button (TOP position)
    this.createTouchButton(
      'right',
      width * positions.MOVEMENT_X,
      height * positions.MOVEMENT_TOP_Y,
      buttonSize,
      '→',
      0x32CD32, // Green for forward
      () => {
        this.mobileControls.right = true;
        this.hapticFeedback('light');
      },
      () => this.mobileControls.right = false
    );
    
    // Backward/Left movement button (BOTTOM position)
    this.createTouchButton(
      'left',
      width * positions.MOVEMENT_X,
      height * positions.MOVEMENT_BOTTOM_Y,
      buttonSize,
      '←',
      0xFF6347, // Red for backward
      () => {
        this.mobileControls.left = true;
        this.hapticFeedback('light');
      },
      () => this.mobileControls.left = false
    );
    
    // ACTION CONTROLS (Right side) - BIGGER and Well separated
    // Jump button (lower position)
    this.createTouchButton(
      'jump',
      width * positions.ACTION_X,
      height * positions.JUMP_Y,
      buttonSize,
      '↑',
      0xFF69B4, // Pink jump
      () => {
        this.jump();
        this.hapticFeedback('medium');
      },
      () => {} // No release action
    );
    
    // Special ability button (much higher up and bigger)
    this.createTouchButton(
      'ability',
      width * positions.ACTION_X,
      height * positions.ABILITY_Y,
      buttonSize * 0.9, // Slightly smaller than movement/jump
      '✨',
      0xFFD700, // Gold ability
      () => {
        this.useSpecialAbility();
        this.hapticFeedback('heavy');
      },
      () => {} // No release action
    );
    
    console.log('📱 Enhanced BIGGER mobile controls created with improved spacing');
  }

  hapticFeedback(type = 'light') {
    if (!this.isMobile || !navigator.vibrate) return;
    
    const patterns = {
      light: 30,
      medium: 50,
      heavy: [50, 30, 50]
    };
    
    navigator.vibrate(patterns[type] || patterns.light);
  }

  createTouchButton(name, x, y, size, text, color, onPress, onRelease) {
    // Enhanced button background with better visibility and bigger size
    const button = this.scene.add.circle(x, y, size/2, color, GAME_CONFIG.MOBILE.BUTTON_ALPHA);
    button.setScrollFactor(0);
    button.setDepth(1000);
    button.setClass = 'mobile-control-button'; // For CSS targeting
    
    // Enhanced border with gradient effect - THICKER for bigger buttons
    const border = this.scene.add.circle(x, y, size/2, 0x000000, 0);
    border.setStrokeStyle(5, 0xFFFFFF, 0.9); // Thicker border
    border.setScrollFactor(0);
    border.setDepth(1001);
    
    // Inner highlight for 3D effect - BIGGER
    const highlight = this.scene.add.circle(x, y - size/8, size/3, 0xFFFFFF, 0.3);
    highlight.setScrollFactor(0);
    highlight.setDepth(1002);
    
    // Button text/icon with BIGGER sizing
    const textSize = Math.max(22, size / 2.2); // Bigger text
    const buttonText = this.scene.add.text(x, y, text, {
      fontSize: `${textSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 3 // Thicker stroke
    }).setOrigin(0.5);
    buttonText.setScrollFactor(0);
    buttonText.setDepth(1003);
    
    // Enhanced hit area for better touch response - BIGGER
    const hitArea = this.scene.add.circle(x, y, size/1.6, 0x000000, 0); // Bigger hit area
    hitArea.setScrollFactor(0);
    hitArea.setDepth(999);
    hitArea.setInteractive();
    
    // Enhanced touch events with better feedback
    hitArea.on('pointerdown', (pointer) => {
      // Visual feedback - more pronounced for bigger buttons
      button.setScale(0.9);
      border.setScale(0.9);
      highlight.setScale(0.9);
      buttonText.setScale(0.9);
      
      // Enhanced color feedback
      button.setTint(0xDDDDDD);
      
      onPress();
    });
    
    hitArea.on('pointerup', () => {
      this.resetButtonVisuals(button, border, highlight, buttonText);
      onRelease();
    });
    
    hitArea.on('pointerout', () => {
      this.resetButtonVisuals(button, border, highlight, buttonText);
      onRelease();
    });
    
    // Store all button components
    this.touchButtons[name] = {
      button: button,
      border: border,
      highlight: highlight,
      text: buttonText,
      hitArea: hitArea,
      x: x,
      y: y,
      size: size
    };
    
    return this.touchButtons[name];
  }

  createTouchButton(name, x, y, size, text, color, onPress, onRelease) {
    // Button background
    const button = this.scene.add.circle(x, y, size/2, color, GAME_CONFIG.MOBILE.BUTTON_ALPHA);
    button.setScrollFactor(0); // Stay fixed on screen
    button.setDepth(1000); // Always on top
    
    // Button border
    const border = this.scene.add.circle(x, y, size/2, 0x000000, 0);
    border.setStrokeStyle(3, 0xFFFFFF, 0.8);
    border.setScrollFactor(0);
    border.setDepth(1001);
    
    // Button text/icon
    const textSize = Math.max(16, size / 3);
    const buttonText = this.scene.add.text(x, y, text, {
      fontSize: `${textSize}px`,
      fontFamily: 'Arial, sans-serif',
      fontWeight: 'bold',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    buttonText.setScrollFactor(0);
    buttonText.setDepth(1002);
    
    // Make button interactive
    const hitArea = this.scene.add.circle(x, y, size/2, 0x000000, 0);
    hitArea.setScrollFactor(0);
    hitArea.setDepth(999);
    hitArea.setInteractive();
    
    // Touch events
    hitArea.on('pointerdown', (pointer) => {
      // Visual feedback
      button.setScale(0.9);
      border.setScale(0.9);
      buttonText.setScale(0.9);
      
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      onPress();
    });
    
    hitArea.on('pointerup', () => {
      // Reset visual state
      button.setScale(1);
      border.setScale(1);
      buttonText.setScale(1);
      
      onRelease();
    });
    
    hitArea.on('pointerout', () => {
      // Reset visual state when finger leaves button
      button.setScale(1);
      border.setScale(1);
      buttonText.setScale(1);
      
      onRelease();
    });
    
    // Store button components
    this.touchButtons[name] = {
      button: button,
      border: border,
      text: buttonText,
      hitArea: hitArea
    };
    
    return this.touchButtons[name];
  }

  update(time, delta) {
    const player = this.scene.getPlayer();
    if (!player || !player.active) return;
    
    this.updatePlayerMovement();
    this.updatePlayerVisuals();
    this.updateMobileControls();
  }

  updatePlayerMovement() {
    const player = this.scene.getPlayer();
    if (!player || !player.active) return;
    
    const { left, right, up } = this.cursors;
    const { left: mobileLeft, right: mobileRight } = this.mobileControls;
    
    // Enhanced ground detection
    const wasGrounded = player.isGrounded;
    player.isGrounded = player.body.touching.down || player.body.blocked.down;
    
    // Reset jump count when landing
    if (player.isGrounded && !wasGrounded) {
      player.jumpCount = 0;
    }
    
    // Enhanced horizontal movement (keyboard + mobile)
    if (left.isDown || this.wasdKeys.A.isDown || mobileLeft) {
      player.setVelocityX(-GAME_CONFIG.PHYSICS.PLAYER_SPEED);
      player.setFlipX(true);
    } else if (right.isDown || this.wasdKeys.D.isDown || mobileRight) {
      player.setVelocityX(GAME_CONFIG.PHYSICS.PLAYER_SPEED);
      player.setFlipX(false);
    } else {
      player.setVelocityX(player.body.velocity.x * 0.8);
    }
    
    // Enhanced double jump system (keyboard + mobile)
    if (Phaser.Input.Keyboard.JustDown(up) || Phaser.Input.Keyboard.JustDown(this.wasdKeys.W)) {
      if (player.isGrounded || player.jumpCount < player.maxJumps) {
        this.jump();
      }
    }
    
    // Special ability (keyboard only - mobile has button)
    if (Phaser.Input.Keyboard.JustDown(this.wasdKeys.SPACE) && gameState.abilityReady) {
      this.useSpecialAbility();
    }
  }

  updatePlayerVisuals() {
    const player = this.scene.getPlayer();
    if (!player || !player.active) return;
    
    // Enhanced invincibility visual effect
    if (player.isInvincible) {
      player.alpha = 0.5 + Math.sin(this.scene.time.now * 0.02) * 0.5;
    } else {
      player.alpha = 1;
    }
  }

  updateMobileControls() {
    if (!this.isMobile) return;
    
    // Update button visibility based on ability cooldown
    if (this.touchButtons.ability) {
      const abilityButton = this.touchButtons.ability;
      if (gameState.abilityReady) {
        abilityButton.button.setFillStyle(0xFFD700, GAME_CONFIG.MOBILE.BUTTON_ALPHA); // Gold when ready
        abilityButton.border.setStrokeStyle(3, 0xFFFFFF, 0.8);
        abilityButton.text.setStyle({ color: '#FFFFFF' });
      } else {
        abilityButton.button.setFillStyle(0x808080, GAME_CONFIG.MOBILE.BUTTON_ALPHA); // Gray when on cooldown
        abilityButton.border.setStrokeStyle(3, 0x666666, 0.5);
        abilityButton.text.setStyle({ color: '#CCCCCC' });
      }
    }
    
    // Update button positions on screen resize
    this.updateButtonPositions();
  }

  updateButtonPositions() {
    if (!this.isMobile || !this.touchButtons) return;
    
    const { width, height } = this.scene.scale;
    const isLandscape = width > height;
    const positions = isLandscape ? 
      GAME_CONFIG.MOBILE.POSITIONS.LANDSCAPE : 
      GAME_CONFIG.MOBILE.POSITIONS.PORTRAIT;
    
    // Recalculate button size for new orientation
    const baseButtonSize = Math.min(
      GAME_CONFIG.MOBILE.BUTTON_SIZE,
      Math.min(width, height) / GAME_CONFIG.MOBILE.LANDSCAPE.BUTTON_SCALE_FACTOR
    );
    const buttonSize = Math.max(baseButtonSize, 80); // Minimum 80px
    
    // Update all buttons with new positions
    const buttonPositions = {
      right: { x: width * positions.MOVEMENT_X, y: height * positions.MOVEMENT_TOP_Y },
      left: { x: width * positions.MOVEMENT_X, y: height * positions.MOVEMENT_BOTTOM_Y },
      jump: { x: width * positions.ACTION_X, y: height * positions.JUMP_Y },
      ability: { x: width * positions.ACTION_X, y: height * positions.ABILITY_Y }
    };
    
    Object.keys(buttonPositions).forEach(buttonName => {
      if (this.touchButtons[buttonName]) {
        const pos = buttonPositions[buttonName];
        this.updateButtonPosition(buttonName, pos.x, pos.y, buttonSize);
      }
    });
    
    console.log(`📱 BIGGER button positions updated for ${isLandscape ? 'landscape' : 'portrait'} (${buttonSize}px)`);
  }


  updateButtonPosition(buttonName, x, y) {
    const button = this.touchButtons[buttonName];
    if (button) {
      button.button.setPosition(x, y);
      button.border.setPosition(x, y);
      button.text.setPosition(x, y);
      button.hitArea.setPosition(x, y);
    }
  }

  jump() {
    const player = this.scene.getPlayer();
    if (!player || !player.active) return;
    
    // Enhanced double jump
    if (player.isGrounded || player.jumpCount < player.maxJumps) {
      player.setVelocityY(GAME_CONFIG.PHYSICS.JUMP_FORCE);
      player.jumpCount++;
      
      // Record double jump for stats
      if (player.jumpCount > 1) {
        gameState.recordDoubleJump();
      }
      
      // Different jump effects for single vs double jump
      if (player.jumpCount === 1) {
        this.scene.getEffectsManager().createJumpEffect(player.x, player.y);
      } else {
        this.scene.getEffectsManager().createDoubleJumpEffect(player.x, player.y);
      }
      
      // Mobile haptic feedback
      if (this.isMobile && navigator.vibrate) {
        navigator.vibrate(player.jumpCount === 1 ? 50 : 100);
      }
      
      /* console.log(`🦘 Jump ${player.jumpCount}/${player.maxJumps}`); */
    }
  }

  useSpecialAbility() {
    const player = this.scene.getPlayer();
    if (!player || !player.active) return;
    
    if (!gameState.useAbility()) return;
    
    // Mobile haptic feedback
    if (this.isMobile && navigator.vibrate) {
      navigator.vibrate([100, 50, 100]); // Pattern for ability
    }
    
    if (gameState.character === 'BRIDE') {
      this.veilGlide();
    } else {
      this.hatShield();
    }
  }

  veilGlide() {
    const player = this.scene.getPlayer();
    if (!player || !player.active) return;
    
    player.body.setGravityY(-400);
    player.setTint(0xFFFFFF);
    
    // Veil trail effect
    const veilTrail = this.scene.time.addEvent({
      delay: 50,
      callback: () => {
        if (player.active) {
          const trail = this.scene.add.circle(player.x - 15, player.y, 3, 0xFFFFFF, 0.6);
          this.scene.tweens.add({
            targets: trail,
            alpha: 0,
            scale: 2,
            duration: 1000,
            onComplete: () => {
              if (trail && trail.active) {
                trail.destroy();
              }
            }
          });
        }
      },
      repeat: 40
    });
    
    this.scene.time.delayedCall(2000, () => {
      if (player && player.active) {
        player.body.setGravityY(0);
        player.clearTint();
      }
      veilTrail.destroy();
    });
  }

  hatShield() {
    const player = this.scene.getPlayer();
    if (!player || !player.active) return;
    
    player.isInvincible = true;
    player.setTint(0x4169E1);
    
    // Shield effect
    const shield = this.scene.add.circle(player.x, player.y, 25, 0x4169E1, 0.3);
    shield.setStrokeStyle(3, 0x4169E1);
    
    this.scene.tweens.add({
      targets: shield,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 1500,
      yoyo: true,
      onComplete: () => {
        if (shield && shield.active) {
          shield.destroy();
        }
      }
    });
    
    this.scene.time.delayedCall(1500, () => {
      if (player && player.active) {
        player.isInvincible = false;
        player.clearTint();
      }
    });
  }

  // Handle screen orientation changes
  handleOrientationChange() {
    if (this.isMobile) {
      // Slight delay to let orientation settle
      this.scene.time.delayedCall(200, () => {
        this.updateButtonPositions();
      });
    }
  }

  // Show/hide mobile controls
  setMobileControlsVisible(visible) {
    if (!this.isMobile) return;
    
    Object.values(this.touchButtons).forEach(button => {
      button.button.setVisible(visible);
      button.border.setVisible(visible);
      button.text.setVisible(visible);
      button.hitArea.setVisible(visible);
    });
  }

  // Cleanup mobile controls
  destroyMobileControls() {
    Object.values(this.touchButtons).forEach(button => {
      button.button.destroy();
      button.border.destroy();
      button.text.destroy();
      button.hitArea.destroy();
    });
    this.touchButtons = {};
  }

  // Get current input state for debugging
  getInputState() {
    return {
      keyboard: {
        left: this.cursors.left.isDown || this.wasdKeys.A.isDown,
        right: this.cursors.right.isDown || this.wasdKeys.D.isDown,
        up: this.cursors.up.isDown || this.wasdKeys.W.isDown,
        space: this.wasdKeys.SPACE.isDown
      },
      mobile: this.mobileControls,
      isMobile: this.isMobile
    };
  }
}